import { Injectable } from '@angular/core';
import { CharacteristicItem } from '../models/characteristic-item';
import { Report } from '../models/report.model';
import { getExposureLabel } from './mappers/exposition.mapper';
import { getGarageTypeLabel } from './mappers/garage-type.mapper';
import { getTransportLabelFromCode } from './mappers/transport.mapper';
import { getViabilityLabel } from './mappers/viability.mapper';

@Injectable({
  providedIn: 'root',
})
export class CharacteristicsService {
  constructor() {}

  initializeCharacteristicsList(
    characteristics: Report.Characteristics,
    propertyType: string,
  ): CharacteristicItem[] {
    if (propertyType === 'AGR') {
      return [
        {
          icon: 'bat_exploitation.svg',
          strValue: characteristics.bienAgricole?.hasFarmBuildings
            ? 'Oui'
            : 'Non',
          label: "Bâtiments d'exploitation",
        },
        {
          icon: 'bat_habitation.svg',
          strValue: characteristics.bienAgricole?.hasResidentialBuilding
            ? 'Oui'
            : 'Non',
          label: "Bâtiment d'habitation",
        },
        {
          icon: 'surface_terres_agricoles.svg',
          numValue: characteristics.bienAgricole?.landAreaHa,
          unit: 'ha',
          label: 'Surface des terres en ha',
        },
        {
          icon: 'surface_bat_exploitation.svg',
          numValue: characteristics.bienAgricole?.farmBuildingsAreaM2,
          unit: 'm²',
          label: "Surface des bâtiments d'exploitation en m²",
        },
        {
          icon: 'surface_bat_habitation.svg',
          numValue: characteristics.bienAgricole?.residentialBuildingsAreaM2,
          unit: 'm²',
          label: "Surface des bâtiments d'habitation en m²",
        },
        {
          icon: 'type_culture.svg',
          strValue: characteristics.bienAgricole?.cropType,
          label: 'Type de culture',
        },
      ];
    }

    if (propertyType === 'APP') {
      return [
        {
          icon: 'surface_habitable.svg',
          numValue: characteristics.appartement?.livingAreaM2,
          unit: 'm²',
          label: 'Surface',
        },
        {
          icon: 'surface_sejour.svg',
          numValue: characteristics.appartement?.livingRoomAreaM2,
          unit: 'm²',
          label: 'Surface séjour',
        },
        {
          icon: 'exposition.svg',
          strValue: getExposureLabel(characteristics.appartement?.exposure),
          label: 'Exposition',
        },
        {
          icon: 'nb_pieces.svg',
          strValue: characteristics.appartement?.numberOfRooms,
          label: 'NB pièces',
        },
        {
          icon: 'chambres.svg',
          strValue: characteristics.appartement?.numberOfBedrooms,
          label: 'Chambres',
        },
        {
          icon: 'sbd.svg',
          strValue: characteristics.appartement?.numberOfBathrooms,
          label: 'Salles de bain',
        },
        {
          icon: 'chauffage.svg',
          strValue: characteristics.appartement?.heating,
          label: 'Chauffage',
        },
        {
          icon: 'ges.svg',
          strValue: characteristics.appartement?.ghgEmissions,
          label: 'GES',
        },
        {
          icon: 'dpe.svg',
          strValue: characteristics.appartement?.energyPerformance,
          label: 'DPE',
        },
        {
          icon: 'niveau.svg',
          strValue: characteristics.appartement?.floor,
          label: 'Étage',
        },
      ];
    }

    if (propertyType === 'GAR') {
      return [
        {
          icon: 'garage.svg',
          strValue: getGarageTypeLabel(
            characteristics.garage?.garageType as string,
          ),
          label: 'Type de garage',
        },
        {
          icon: 'nb_parking.svg',
          numValue: characteristics.garage?.numberOfParkingSlots,
          label: "Nombre d'emplacements",
        },
        {
          icon: 'sous_sol.svg',
          strValue: characteristics.garage?.floor,
          label: 'Étage',
        },
      ];
    }

    if (propertyType === 'IMM') {
      return [
        {
          icon: 'nbre_lots.svg',
          numValue: characteristics.immeuble?.numberOfUnits,
          label: 'Nombre logements / lots',
        },
        {
          icon: 'ascenseur.svg',
          strValue: characteristics.immeuble?.hasElevator ? 'Oui' : 'Non',
          label: 'Ascenseur',
        },
        {
          icon: 'copropriete.svg',
          strValue: characteristics.immeuble?.hasCondominium ? 'Oui' : 'Non',
          label: 'Copropriété',
        },
        {
          icon: 'rentabilite.svg',
          numValue: characteristics.immeuble?.profitability,
          label: 'Rentabilité',
          unit: '%',
        },
        {
          icon: 'surface_terrain.svg',
          numValue: characteristics.immeuble?.totalAreaM2,
          label: 'Surface totale en m²',
          unit: 'm²',
        },
        {
          icon: 'usage.svg',
          strValue: characteristics.immeuble?.premisesUsage,
          label: 'Usage des locaux',
        },
      ];
    }

    if (propertyType === 'LAC') {
      return [
        {
          icon: 'surface_terrain.svg',
          numValue: characteristics.localActivite?.areaM2,
          unit: 'm²',
          label: 'Surface en m²',
        },
        {
          icon: 'usage.svg',
          strValue: characteristics.localActivite?.nature,
          label: 'Nature',
        },
        {
          icon: 'stationnement.svg',
          numValue: characteristics.localActivite?.numberOfParkingSpaces,
          label: 'Nombre de stationnements',
        },
        {
          icon: 'transport.svg',
          strValue: characteristics.localActivite?.publicTransportNearby
            .map((transport) =>
              getTransportLabelFromCode(
                transport as Report.Characteristics.Transport,
              ),
            )
            .join(', '),
          label: 'Transport en commun à proximité',
        },
      ];
    }

    if (propertyType === 'MAI') {
      return [
        {
          icon: 'surface_habitable.svg',
          numValue: characteristics.maison?.livingAreaM2,
          unit: 'm²',
          label: 'Surface habitable en m²',
        },
        {
          icon: 'surface_sejour.svg',
          numValue: characteristics.maison?.livingRoomAreaM2,
          unit: 'm²',
          label: 'Surface séjour en m²',
        },
        {
          icon: 'exposition.svg',
          strValue: getExposureLabel(characteristics.maison?.exposure),
          label: 'Exposition',
        },
        {
          icon: 'nb_pieces.svg',
          strValue: characteristics.maison?.numberOfRooms,
          label: 'NB pièces',
        },
        {
          icon: 'chambres.svg',
          strValue: characteristics.maison?.numberOfBedrooms,
          label: 'NB chambres',
        },
        {
          icon: 'surface_terrain.svg',
          numValue: characteristics.maison?.landAreaM2,
          unit: 'm²',
          label: 'Surface terrain en m²',
        },
        {
          icon: 'sbd.svg',
          strValue: characteristics.maison?.numberOfBathrooms,
          label: 'NB salles de bain',
        },
        {
          icon: 'chauffage.svg',
          strValue: characteristics.maison?.heating,
          label: 'Chauffage',
        },
        {
          icon: 'ges.svg',
          strValue: characteristics.maison?.ghgEmissions,
          label: 'GES',
        },
        {
          icon: 'dpe.svg',
          strValue: characteristics.maison?.energyPerformance,
          label: 'DPE',
        },
        {
          icon: 'niveau.svg',
          strValue: characteristics.maison?.level,
          label: 'Niveau',
        },
      ];
    }

    if (propertyType === 'TER') {
      return [
        {
          icon: 'surface_terrain.svg',
          numValue: characteristics.terrain?.areaM2,
          unit: 'm²',
          label: 'Surface en m²',
        },
        {
          icon: 'largeur_terrain.svg',
          numValue: characteristics.terrain?.facadeWidthM2,
          unit: 'm²',
          label: 'Largeur de façade en m²',
        },
        {
          icon: 'viabilite.svg',
          strValue: getViabilityLabel(
            characteristics.terrain
              ?.viability as Report.Characteristics.Viability,
          ),
          label: 'Viabilité',
        },
        {
          icon: 'cos.svg',
          numValue: characteristics.terrain?.plotRatio,
          label: "Coefficient d'occupation des sols",
        },
        {
          icon: 'accessibilite.svg',
          strValue: characteristics.terrain?.accessibility,
          label: 'Accessibilité',
        },
      ];
    }

    if (propertyType === 'VIG') {
      return [
        {
          icon: 'bat_exploitation.svg',
          strValue: characteristics.bienViticole?.hasFarmBuildings
            ? 'Oui'
            : 'Non',
          label: "Bâtiments d'exploitation",
        },
        {
          icon: 'bat_habitation.svg',
          strValue: characteristics.bienViticole?.hasResidentialBuilding
            ? 'Oui'
            : 'Non',
          label: "Bâtiment d'habitation",
        },
        {
          icon: 'surface_vignes.svg',
          numValue: characteristics.bienViticole?.vineyardAreaHa,
          unit: 'ha',
          label: 'Surface du vignoble en ha',
        },
        {
          icon: 'surface_bat_exploitation.svg',
          numValue: characteristics.bienViticole?.farmBuildingsAreaM2,
          unit: 'm²',
          label: "Surface des bâtiments d'exploitation en m²",
        },
        {
          icon: 'surface_bat_habitation.svg',
          numValue: characteristics.bienViticole?.residentialBuildingsAreaM2,
          unit: 'm²',
          label: "Surface des bâtiments d'habitation en m²",
        },
        {
          icon: 'type_cepages.svg',
          strValue: characteristics.bienViticole?.grapeVarieties,
          label: 'Cépages',
        },
        {
          icon: 'aoc.svg',
          strValue: characteristics.bienViticole?.appellation,
          label: 'Appellation',
        },
      ];
    }

    return [];
  }
}
